import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useBusinessLayer, usePaymentMethod } from '@mopla/business-logic';
import { useLogger } from '@mopla/logger';
import { MplButton } from '@mopla/ui';
import { getCountdownTimer$ } from '@mopla/utils';

import { usePayByBankSetupStore } from '../../stores/usePayByBankSetupStore';
import {
	createPayByBankSetupPolling$,
	fetchPaymentMethodSetupStatus,
	PAY_BY_BANK_COUNTDOWN_SECONDS,
	PAY_BY_BANK_GRACE_MS,
	TPaymentMethodSetupStatus,
	UnexpectedSetupStatusError,
} from '../../utils/payByBankSetupPolling';
import { usePaymentPortalControls } from '../../utils/usePaymentPortalControls';
import { PaymentErrorModal, PaymentLoadingOverlay } from '../common';

import { Countdown } from './PayByBankSetupWaiting.styles';

const formatCountdown = (totalSeconds: number) => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/** Full-screen waiting state after returning from the bank (Pay by Bank setup). */
export const PayByBankSetupWaiting: FC = () => {
	const { t } = useTranslation('payment');
	const logger = useLogger('payment');
	const { api } = useBusinessLayer();
	const { syncPaymentMethod } = usePaymentMethod();
	const { openPaymentPortal } = usePaymentPortalControls();
	const { phase, startedAt, markFailed, reset } = usePayByBankSetupStore(
		useShallow((state) => ({
			phase: state.phase,
			startedAt: state.startedAt,
			markFailed: state.markFailed,
			reset: state.reset,
		}))
	);

	/** Keep the polling effect keyed on the waiting phase only, so re-renders never restart it. */
	const latest = useRef({ api, syncPaymentMethod, logger, markFailed, reset });
	latest.current = { api, syncPaymentMethod, logger, markFailed, reset };

	const [remainingSeconds, setRemainingSeconds] = useState(
		PAY_BY_BANK_COUNTDOWN_SECONDS
	);

	useEffect(() => {
		if (phase !== 'waiting' || startedAt === null) return;
		const subscription = getCountdownTimer$(
			new Date(startedAt),
			PAY_BY_BANK_COUNTDOWN_SECONDS
		).subscribe((duration) => setRemainingSeconds(duration.asSeconds()));
		return () => subscription.unsubscribe();
	}, [phase, startedAt]);

	useEffect(() => {
		if (phase !== 'waiting' || startedAt === null) return;
		const { logger: log } = latest.current;
		const deadlineMs = Math.max(
			0,
			startedAt +
				PAY_BY_BANK_COUNTDOWN_SECONDS * 1000 +
				PAY_BY_BANK_GRACE_MS -
				Date.now()
		);
		log.info('[PayByBankSetupWaiting]: start polling setup status');

		const fetchStatus = async (): Promise<TPaymentMethodSetupStatus> => {
			try {
				return await fetchPaymentMethodSetupStatus((url) =>
					latest.current.api.get(url)
				);
			} catch (error) {
				if (error instanceof UnexpectedSetupStatusError) {
					latest.current.logger.error(
						'[PayByBankSetupWaiting]: unexpected setup status response',
						error
					);
				}
				throw error;
			}
		};

		const subscription = createPayByBankSetupPolling$(fetchStatus, {
			deadlineMs,
		}).subscribe(async (result) => {
			const current = latest.current;
			current.logger.info('[PayByBankSetupWaiting]: setup result', { result });
			if (result === 'FAILED') {
				current.markFailed();
				return;
			}
			try {
				await current.syncPaymentMethod();
			} catch (error) {
				current.logger.error(
					'[PayByBankSetupWaiting]: syncPaymentMethod failed',
					error
				);
			}
			current.reset();
		});

		return () => subscription.unsubscribe();
	}, [phase, startedAt]);

	if (phase === 'waiting') {
		return (
			<PaymentLoadingOverlay
				testId="PayByBankSetupWaiting-Overlay"
				fullScreen
				text={t('pay_by_bank_setup.pending_title')}
			>
				<Countdown variant="h2">{formatCountdown(remainingSeconds)}</Countdown>
				<Countdown>{t('pay_by_bank_setup.pending_text')}</Countdown>
			</PaymentLoadingOverlay>
		);
	}

	if (phase === 'failed') {
		return (
			<PaymentErrorModal
				testId="PayByBankSetupWaiting-ErrorModal"
				title={t('pay_by_bank_setup.failed_title')}
				description={t('pay_by_bank_setup.failed_text')}
				onClose={reset}
				actions={
					<MplButton
						testId="PayByBankSetupWaiting-ErrorModal-ChooseAnotherButton"
						fullWidth
						onClick={() => {
							reset();
							openPaymentPortal();
						}}
					>
						{t('pay_by_bank_setup.choose_another')}
					</MplButton>
				}
			/>
		);
	}

	return null;
};
